import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastContainer } from 'react-toastify';

import { supabase } from './lib/supabase';
import { PopupProvider } from './contexts/PopupContext';
import { NotificationSettingsProvider } from './contexts/NotificationSettingsContext';
import PopupManager from './components/popups/PopupManager';
import NotificationToastListener from './components/notifications/NotificationToastListener/NotificationToastListener';
import { useEventStatusChecker } from './hooks/useEventStatusChecker';
import LandingPage from './pages/LandingPage';
import PrivateLayout from './components/layout/PrivateLayout';
import DiscoverPage from './pages/DiscoverPage';
import MyEventsPage from './pages/MyEventsPage';
import NotificationsPage from './pages/NotificationsPage';
import ProfilePage from './pages/ProfilePage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import EventInvitePage from './pages/EventInvitePage';
import InviteHandler from './components/InviteHandler';
import Loading from './components/layout/Loading';
import './styles/index.css';
import './styles/scrollbar.css';
import './styles/toastify-layout.css';

function App() {
  const { i18n } = useTranslation('shared');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('discover');
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [inviteEventId, setInviteEventId] = useState(null);
  const betaRankAssignedRef = useRef(new Set());

  // Użyj hooka do sprawdzania statusu wydarzeń (tylko dla zalogowanych użytkowników)
  useEventStatusChecker(session?.user?.id);

  useEffect(() => {
    // Sprawdź czy w URL hash jest token resetu hasła
    const checkResetPassword = () => {
      const hash = window.location.hash;
      if (hash && hash.includes('type=recovery')) {
        setIsResetPassword(true);
        // Hash pozostaje w URL - Supabase potrzebuje go do weryfikacji tokenu
      }
    };

    // Sprawdź czy URL to /invite/:eventId
    const checkInviteUrl = () => {
      const path = window.location.pathname;
      const inviteMatch = path.match(/^\/invite\/([^/]+)$/);
      if (inviteMatch) {
        setInviteEventId(inviteMatch[1]);
      } else {
        setInviteEventId(null);
      }
    };

    checkResetPassword();
    checkInviteUrl();

    // Nasłuchuj zmian URL (np. przy nawigacji wstecz)
    const handlePopState = () => {
      checkInviteUrl();
    };
    window.addEventListener('popstate', handlePopState);

    // Sprawdź aktualną sesję przy starcie
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    // Nasłuchuj zmian w autoryzacji (login/logout)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      // Wyczyść sessionStorage przy wylogowaniu
      if (event === 'SIGNED_OUT' || !session) {
        sessionStorage.removeItem('discoverViewMode');
      }
    });

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleAuthSuccess = (session) => {
    setSession(session);
    // InviteHandler automatycznie sprawdzi sessionStorage i otworzy popup
  };

  const handleLogout = () => {
    setSession(null);
    setActiveTab('discover'); // Reset to default tab
    // Wyczyść sessionStorage przy wylogowaniu
    sessionStorage.removeItem('discoverViewMode');
  };

  const handlePasswordResetSuccess = () => {
    // Po udanym resecie hasła, wyczyść hash i przeładuj
    setIsResetPassword(false);
    window.location.hash = '';
    // Jeśli użytkownik był zalogowany, odśwież sesję
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
  };

  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId || betaRankAssignedRef.current.has(userId)) {
      return;
    }

    let cancelled = false;

    const ensureBetaTesterRank = async () => {
      try {
        const { error } = await supabase
          .from('users')
          .update({ is_beta_tester: true })
          .eq('id', userId)
          .neq('is_beta_tester', true);

        if (error && !cancelled) {
          console.warn('assign beta tester rank:', error.message || error);
          return;
        }

        if (!cancelled) {
          betaRankAssignedRef.current.add(userId);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('assign beta tester rank failed:', err);
        }
      }
    };

    ensureBetaTesterRank();

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const renderPrivateContent = () => {
    let main = null;
    switch (activeTab) {
      case 'discover':
        main = <DiscoverPage />;
        break;
      case 'events':
        main = <MyEventsPage />;
        break;
      case 'notifications':
        main = null;
        break;
      case 'profile':
        main = (
          <ProfilePage
            onLogout={handleLogout}
            onNavigateToMyEvents={(subtab) => {
              try {
                sessionStorage.setItem('myEventsInitialSubtab', subtab);
              } catch (_) {
                /* ignore */
              }
              setActiveTab('events');
            }}
            onNavigateToDiscover={() => setActiveTab('discover')}
          />
        );
        break;
      default:
        main = <DiscoverPage />;
    }

    return (
      <>
        {main}
        <div
          className="app-tab-persist-notifications"
          hidden={activeTab !== 'notifications'}
          aria-hidden={activeTab !== 'notifications'}
        >
          <NotificationsPage
            isActive={activeTab === 'notifications'}
            onNavigateTab={setActiveTab}
          />
        </div>
      </>
    );
  };

  // Jeśli jest token resetu hasła w URL, wyświetl ResetPasswordPage
  // (niezależnie od tego czy użytkownik jest zalogowany czy nie)
  if (isResetPassword) {
    return (
      <div className="App">
        <ResetPasswordPage 
          key={i18n.language} // Key oparty na języku wymusza re-render przy zmianie języka
          onPasswordResetSuccess={handlePasswordResetSuccess} 
        />
      </div>
    );
  }

  // Jeśli jest URL zaproszenia i użytkownik nie jest zalogowany, wyświetl EventInvitePage
  // Jeśli użytkownik jest zalogowany, renderuj normalną aplikację i otwórz popup
  if (inviteEventId && !session && !loading) {
    return (
      <PopupProvider>
        <div className="App">
          <EventInvitePage eventId={inviteEventId} onAuthSuccess={handleAuthSuccess} />
          <PopupManager />
          <ToastContainer />
        </div>
      </PopupProvider>
    );
  }

  if (loading) {
    return <Loading fullScreen size="large" />;
  }

  return (
    <PopupProvider>
      <div className="App">
        {session ? (
          <NotificationSettingsProvider>
            <NotificationToastListener userId={session.user.id} />
            <InviteHandler inviteEventId={inviteEventId} session={session} />
            <PrivateLayout 
              activeTab={activeTab} 
              setActiveTab={setActiveTab}
              onLogout={handleLogout}
            >
              {renderPrivateContent()}
            </PrivateLayout>
            <PopupManager />
          </NotificationSettingsProvider>
        ) : (
          <>
            <LandingPage onAuthSuccess={handleAuthSuccess} />
            <PopupManager />
          </>
        )}
        <ToastContainer />
      </div>
    </PopupProvider>
  );
}

export default App;
